import { useMemo, useState } from 'react';
import './create-league.styles.scss';
import { useNavigate } from 'react-router-dom';
import { usePapaParse } from 'react-papaparse';
import {
    CONFERENCE_MAX, CONFERENCE_MIN, CONFERENCE_TOURNAMENT_SIZES, LEAGUE_TYPES,
    PLAYOFF_SIZES, buildLeagueSettings, conferenceSizes, playoffRounds,
} from '@shared/leagueTypes.js';
import { TEAM_CSV_TEMPLATE, buildTeamList } from '@shared/teamList.js';
import { UseCloudFunction } from '@/utils/firebase.utils';
import BackButton from '@/components/SHARED/back-button/back-button.component';
import ThreeDotsLoader from '@/components/loader/three-dots-loader.component';

const FIELD_OPTIONS = { college: [16, 32, 64], pro: PLAYOFF_SIZES };

function defaultsFor(type) {
    const p = LEAGUE_TYPES[type];
    return {
        teamCount: p.teamCount,
        conferenceCount: p.conferenceCount,
        gamesPerTeam: p.gamesPerTeam,
        conferenceGames: p.conferenceGames,
        fieldSize: p.postseason.fieldSize,
        conferenceTournamentSize: p.postseason.conferenceTournamentSize ?? 8,
        chemistryStrength: p.chemistryStrength,
    };
}

const CreateLeague = () => {

    const [ name, setName ] = useState('');
    const [ type, setType ] = useState('college');
    const [ source, setSource ] = useState('generate');
    const [ form, setForm ] = useState(defaultsFor('college'));
    const [ upload, setUpload ] = useState(null);
    const [ saving, setSaving ] = useState(false);
    const [ serverErrors, setServerErrors ] = useState(null);
    const { readString } = usePapaParse();
    const navigate = useNavigate();

    function chooseType(t) {
        setType(t);
        setForm(defaultsFor(t));
    }

    function setField(key, value) {
        setForm({ ...form, [key]: value });
    }

    const teamCount = source === 'upload' ? (upload?.list.teams.length ?? 0) : Number(form.teamCount);
    const fieldChoices = useMemo(() =>
        FIELD_OPTIONS[type].filter(n => n <= Math.max(2, teamCount / 2)), [type, teamCount]);

    const overrides = useMemo(() => {
        const o = {
            gamesPerTeam: Number(form.gamesPerTeam),
            conferenceGames: Number(form.conferenceGames),
            chemistryStrength: Number(form.chemistryStrength),
        };
        if (source === 'generate') {
            o.teamCount = Number(form.teamCount);
            o.conferenceCount = Number(form.conferenceCount);
        } else if (upload) {
            o.teamCount = upload.list.teams.length;
            o.conferenceCount = upload.list.conferences.length;
        }
        const field = fieldChoices.includes(Number(form.fieldSize)) ?
            Number(form.fieldSize) : fieldChoices.at(-1);
        o.postseason = { fieldSize: field };
        if (type === 'pro') {
            o.postseason.seriesLengths = LEAGUE_TYPES.pro.postseason.seriesLengths.slice(-playoffRounds(field));
        } else {
            o.postseason.conferenceTournamentSize = Number(form.conferenceTournamentSize);
        }
        return o;
    }, [form, source, upload, type, fieldChoices]);

    // Same validation the server runs, shown as you type.
    const settingsError = useMemo(() => {
        if (source === 'upload' && !upload) return null;
        try {
            buildLeagueSettings(type, overrides);
            return null;
        } catch (err) {
            return err.message;
        }
    }, [type, overrides, source, upload]);

    const sizes = source === 'generate' && Number(form.conferenceCount) > 0 ?
        conferenceSizes(Number(form.teamCount), Number(form.conferenceCount)) : null;

    function handleFile(file) {
        setServerErrors(null);
        if (!file) return;
        file.text().then(text => {
            readString(text, {
                header: true,
                skipEmptyLines: true,
                transformHeader: h => h.trim().toLowerCase(),
                complete: (results) => {
                    const rows = results.data;
                    setUpload({ fileName: file.name, rows, list: buildTeamList(rows) });
                },
            });
        });
    }

    async function submit() {
        setSaving(true);
        setServerErrors(null);
        const res = await UseCloudFunction('createLeague', {
            name: name.trim(),
            type,
            overrides,
            // Daily game times start out in the admin's own time zone.
            timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            ...(source === 'upload' && { rows: upload.rows }),
        });
        setSaving(false);
        if (res.error) {
            setServerErrors(res.problems?.length ? res.problems : [res.error]);
            return;
        }
        navigate(`/admin/leagues/${res.league.id}`);
    }

    const uploadErrors = upload?.list.errors ?? [];
    const canSubmit = name.trim() && !settingsError && !saving &&
        (source === 'generate' || (upload && uploadErrors.length === 0));

    return (
        <div className='create-league'>
            <div className='g-card'>
                <BackButton cancel={() => navigate('/admin/leagues')} />
                <h2>Create a League</h2>

                <div className='field'>
                    <label htmlFor='league-name'>League name</label>
                    <input id='league-name' type='text' maxLength={60} value={name} onChange={(e) => setName(e.target.value)} placeholder='e.g. Hardwood Classic' />
                </div>

                <fieldset className='choice-group'>
                    <legend>League type</legend>
                    {Object.entries(LEAGUE_TYPES).map(([key, preset]) => (
                        <label key={key} className={`choice ${type === key ? 'selected' : ''}`}>
                            <input type='radio' name='league-type' value={key} checked={type === key} onChange={() => chooseType(key)} />
                            <span className='choice-title'>{preset.label}</span>
                            <span className='meta'>
                                {key === 'college' ?
                                    'Wide talent gaps, 4-year careers, conference tournaments, 64-team bracket' :
                                    'Fewer teams, more games, parity, longer careers, playoff series'}
                            </span>
                        </label>
                    ))}
                </fieldset>

                <fieldset className='choice-group'>
                    <legend>Teams</legend>
                    <label className={`choice ${source === 'generate' ? 'selected' : ''}`}>
                        <input type='radio' name='team-source' value='generate' checked={source === 'generate'} onChange={() => setSource('generate')} />
                        <span className='choice-title'>Generate a fictional league</span>
                        <span className='meta'>Made-up schools, mascots and colors</span>
                    </label>
                    <label className={`choice ${source === 'upload' ? 'selected' : ''}`}>
                        <input type='radio' name='team-source' value='upload' checked={source === 'upload'} onChange={() => setSource('upload')} />
                        <span className='choice-title'>Upload a spreadsheet</span>
                        <span className='meta'>Your own teams, from a CSV file</span>
                    </label>
                </fieldset>

                {source === 'generate' &&
                    <div className='form-grid'>
                        <div className='field'>
                            <label htmlFor='team-count'>Number of teams</label>
                            <input id='team-count' type='number' inputMode='numeric' min={CONFERENCE_MIN} max={400} value={form.teamCount} onChange={(e) => setField('teamCount', e.target.value)} />
                        </div>
                        <div className='field'>
                            <label htmlFor='conf-count'>Number of conferences</label>
                            <input id='conf-count' type='number' inputMode='numeric' min={1} value={form.conferenceCount} onChange={(e) => setField('conferenceCount', e.target.value)} />
                        </div>
                        {sizes &&
                            <p className='meta span-all'>
                                {sizes.length} conferences of {Math.min(...sizes) === Math.max(...sizes) ? sizes[0] : `${Math.min(...sizes)}-${Math.max(...sizes)}`} teams.
                                Each conference needs {CONFERENCE_MIN}-{CONFERENCE_MAX} teams.
                            </p>
                        }
                    </div>
                }

                {source === 'upload' &&
                    <div className='upload-box'>
                        <p>
                            One row per team with these columns: <b>conference, name, mascot, abbreviation, color1, color2, prestige, city, state</b>.
                            Colors are hex codes like #1d4ed8. Prestige (1-5) sets how strong a college program is; leave it blank for 3.
                            City and state (a two-letter code like IL) are optional.
                            Each conference needs {CONFERENCE_MIN}-{CONFERENCE_MAX} teams.
                        </p>
                        <a className='g-button small-btn' href={`data:text/csv;charset=utf-8,${encodeURIComponent(TEAM_CSV_TEMPLATE)}`} download='team-template.csv'>
                            Download template
                        </a>
                        <div className='field'>
                            <label htmlFor='team-file'>Team list (CSV)</label>
                            <input id='team-file' type='file' accept='.csv,text/csv' onChange={(e) => handleFile(e.target.files[0])} />
                        </div>
                        {upload &&
                            <div className={`upload-result ${uploadErrors.length ? 'has-errors' : 'ok'}`}>
                                <div><b>{upload.fileName}</b>: {upload.list.teams.length} teams in {upload.list.conferences.length} conferences</div>
                                {upload.list.conferences.length > 0 &&
                                    <ul className='conference-list'>
                                        {upload.list.conferences.map(c => (
                                            <li key={c.id}>{c.name} <span className='meta'>({c.teamIds.length})</span></li>
                                        ))}
                                    </ul>
                                }
                                {uploadErrors.length > 0 &&
                                    <>
                                        <div className='error-title'>Fix these in the spreadsheet and upload it again:</div>
                                        <ul className='error-list'>
                                            {uploadErrors.slice(0, 15).map((e, i) => <li key={i}>{e}</li>)}
                                            {uploadErrors.length > 15 && <li>…and {uploadErrors.length - 15} more</li>}
                                        </ul>
                                    </>
                                }
                            </div>
                        }
                    </div>
                }

                <h3>Season</h3>
                <div className='form-grid'>
                    <div className='field'>
                        <label htmlFor='games'>Games per team</label>
                        <input id='games' type='number' inputMode='numeric' min={2} max={120} value={form.gamesPerTeam} onChange={(e) => setField('gamesPerTeam', e.target.value)} />
                    </div>
                    <div className='field'>
                        <label htmlFor='conf-games'>Conference games</label>
                        <input id='conf-games' type='number' inputMode='numeric' min={0} value={form.conferenceGames} onChange={(e) => setField('conferenceGames', e.target.value)} />
                    </div>
                    {type === 'college' &&
                        <div className='field'>
                            <label htmlFor='conf-tourney'>Conference tournament</label>
                            <select id='conf-tourney' value={form.conferenceTournamentSize} onChange={(e) => setField('conferenceTournamentSize', e.target.value)}>
                                {CONFERENCE_TOURNAMENT_SIZES.map(n => <option key={n} value={n}>Top {n} teams</option>)}
                            </select>
                        </div>
                    }
                    <div className='field'>
                        <label htmlFor='field-size'>{type === 'college' ? 'National tournament' : 'Playoff teams'}</label>
                        <select id='field-size' value={overrides.postseason.fieldSize} onChange={(e) => setField('fieldSize', e.target.value)}>
                            {fieldChoices.map(n => <option key={n} value={n}>{n} teams</option>)}
                        </select>
                    </div>
                    {type === 'college' &&
                        <p className='meta span-all'>
                            A conference with fewer teams than its tournament size uses the next size down (a 10-team conference plays an 8-team tournament).
                        </p>
                    }
                </div>

                <details className='advanced'>
                    <summary>Advanced</summary>
                    <div className='form-grid'>
                        <div className='field'>
                            <label htmlFor='chem-strength'>Chemistry strength</label>
                            <input id='chem-strength' type='number' inputMode='decimal' step='0.1' min={0} max={3} value={form.chemistryStrength} onChange={(e) => setField('chemistryStrength', e.target.value)} />
                        </div>
                        <p className='meta span-all'>
                            How much team chemistry swings games. The default is calibrated for this league type; 0 turns chemistry off.
                        </p>
                    </div>
                </details>

                {settingsError && <div className='form-error' role='alert'>{settingsError}</div>}
                {serverErrors &&
                    <div className='form-error' role='alert'>
                        <ul>{serverErrors.map((e, i) => <li key={i}>{e}</li>)}</ul>
                    </div>
                }

                <div className='buttons'>
                    <button className='g-button' onClick={() => navigate('/admin/leagues')}>Cancel</button>
                    <button className={`g-button primary ${canSubmit ? '' : 'disabled'}`} disabled={!canSubmit} onClick={() => submit()}>
                        {saving ? <ThreeDotsLoader /> : 'Create League'}
                    </button>
                </div>
                {saving && <p className='meta saving-note'>Building teams, rosters and the schedule. This takes a few seconds.</p>}
            </div>
        </div>
    )
}

export default CreateLeague
